import Box from '@mui/material/Box';
import LinearProgress from '@mui/material/LinearProgress';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import {
  etaSeconds,
  pastEstimate,
  percentDone,
  producedRows,
} from '@platform/schema/generation';

import { elapsedOf, formatDuration } from './stage';

import type {
  GenerationDetail,
  GenerationJob,
  GenerationRow,
  JobStatus,
  RowStatus,
  RowTally,
} from '@platform/schema/generation';

/**
 * Where a bulk import stands — GEN-92, and the screen the acceptance criteria describe.
 *
 * Counts rather than a row list, because the question an operator has while fifty lessons
 * generate is "how much is left and is any of it broken", and the answer to that is six
 * numbers. The rows below it answer "which one", which is a different question and a
 * scroll away.
 *
 * Every number here is counted from the rows on the server — none of it is bookkeeping the
 * job keeps about itself, so nothing can disagree with the table underneath.
 */

/**
 * The statuses in the words the person who asked for the import uses.
 *
 * `PARTIAL` is two different situations and the console is the only place that can tell
 * them apart: some lessons wrote something and stopped, which is a look; or some produced
 * nothing at all, which is work to redo. A single "partial" label sent an operator to the
 * row table to find out which, every time.
 */
const PLAIN: Record<Exclude<JobStatus, 'PARTIAL'>, string> = {
  QUEUED: 'Queued',
  RUNNING: 'Processing',
  // Not done: the review still to run can block publishing — GEN-121.
  REVIEWING: 'Reviewing',
  SUCCEEDED: 'Completed',
  FAILED: 'Failed',
  CANCELLED: 'Canceled',
};

export function statusLabel(
  job: Pick<GenerationJob, 'status' | 'lessons'>,
): string {
  const { lessons } = job;

  // Picked up with no lesson started: "Processing" beside a tile reading "0 Processing"
  // is two answers to one question. Canceled lessons never started, so they do not count.
  if (
    job.status === 'RUNNING' &&
    lessons.total > 0 &&
    lessons.pending + lessons.skipped === lessons.total
  )
    return 'Starting';

  if (job.status !== 'PARTIAL') return PLAIN[job.status];

  return lessons.failed > 0 ? 'Needs attention' : 'Completed with warnings';
}

/** A row in the word of the tile that counts it, so a "Warnings" row is one of the Warnings. */
export const ROW_PLAIN: Record<RowStatus, string> = {
  PENDING: 'Queued',
  RUNNING: 'Processing',
  SUCCEEDED: 'Completed',
  PARTIAL: 'Warnings',
  FAILED: 'Failed',
  SKIPPED: 'Canceled',
};

/** One number and what it counts. The label is under it, so the eye reads the figures. */
function Tile({ label, value }: { label: string; value: string }) {
  return (
    <Box sx={{ minWidth: 96 }}>
      <Typography variant="h5" component="p">
        {value}
      </Typography>
      <Typography variant="caption" color="text.secondary">
        {label}
      </Typography>
    </Box>
  );
}

/** The review rows in the counters' words, the ones with nothing in them left out. */
function reviewLine(reviews: RowTally): string | undefined {
  if (reviews.total === 0) return undefined;

  const parts = [
    [reviews.running, 'reviewing'],
    [reviews.pending, 'queued'],
    [producedRows(reviews), 'done'],
    [reviews.failed, 'failed'],
    [reviews.skipped, 'canceled'],
  ] as const;

  return `Review: ${parts
    .filter(([count]) => count > 0)
    .map(([count, word]) => `${String(count)} ${word}`)
    .join(', ')}`;
}

type Progress = Pick<
  GenerationDetail,
  'status' | 'lessons' | 'units' | 'reviews' | 'startedAt' | 'finishedAt'
> & { rows: readonly Pick<GenerationRow, 'kind' | 'status' | 'startedAt'>[] };

/**
 * Computed here rather than sent — GEN-92. Everything it reads already crosses the wire,
 * so a field for it would be a third answer to a question two others have, and it would
 * only be as fresh as the last poll.
 */
function timeLeft(
  job: Pick<Progress, 'lessons' | 'startedAt' | 'rows'>,
  now: number,
): string {
  if (job.startedAt === undefined) return '';

  const elapsed = now - Date.parse(job.startedAt);
  const running = job.rows.flatMap((row) =>
    row.kind === 'LESSON' &&
    row.status === 'RUNNING' &&
    row.startedAt !== undefined
      ? [now - Date.parse(row.startedAt)]
      : [],
  );

  if (pastEstimate(job.lessons, elapsed, Math.max(0, ...running)))
    return ' · taking longer than expected';

  const left = etaSeconds(job.lessons, elapsed);

  return left === undefined
    ? ''
    : ` · about ${formatDuration(left * 1000)} left`;
}

export function BulkProgress({
  job,
  now,
}: {
  job: Progress;
  /** The caller's clock, so a settled job stops re-rendering — GEN-68. */
  now: number;
}) {
  const { lessons, units } = job;
  const percent = percentDone(lessons);
  const review = reviewLine(job.reviews);

  // Nothing has been outlined yet, so there is no lesson count to be honest about. The
  // job's own status is on the header; a row of zeroes here would read as fifty failures.
  if (lessons.total === 0) return null;

  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Stack spacing={2}>
        {/* No status chip here: the page header already carries one, and "Processing"
            appearing twice on one screen is two facts to reconcile rather than one. */}
        <Typography variant="h6" component="h2">
          Generating {lessons.total} lesson{lessons.total === 1 ? '' : 's'}
        </Typography>

        <Box>
          <LinearProgress
            variant="determinate"
            value={percent}
            // Announced, because the bar is the only thing on the screen that moves and a
            // screen reader would otherwise be told nothing at all for an hour.
            aria-label={`${String(percent)}% complete — ${String(producedRows(lessons))} of ${String(lessons.total)} lessons written`}
          />
          <Typography variant="caption" color="text.secondary">
            {percent}% complete
          </Typography>
        </Box>

        {/* Counted over the rows in the table, by the status each one shows, so a tile
            can never read 0 beside a row in its own word — GEN-113. */}
        <Stack direction="row" spacing={3} sx={{ flexWrap: 'wrap' }}>
          <Tile label={ROW_PLAIN.SUCCEEDED} value={String(units.succeeded)} />
          <Tile label={ROW_PLAIN.RUNNING} value={String(units.running)} />
          <Tile label={ROW_PLAIN.PENDING} value={String(units.pending)} />
          <Tile label={ROW_PLAIN.PARTIAL} value={String(units.partial)} />
          <Tile label={ROW_PLAIN.FAILED} value={String(units.failed)} />
          {units.skipped > 0 && (
            <Tile label={ROW_PLAIN.SKIPPED} value={String(units.skipped)} />
          )}
        </Stack>

        {review !== undefined && (
          <Typography variant="body2">{review}</Typography>
        )}

        <Typography variant="body2" color="text.secondary">
          {job.startedAt === undefined
            ? 'Not started yet.'
            : `Started ${new Date(job.startedAt).toLocaleString()} · ${elapsedOf(job, now) ?? '—'} elapsed`}
          {timeLeft(job, now)}
        </Typography>

        {/* Said on the screen rather than assumed, because it is the thing this change is
            for: the run does not belong to the browser tab watching it. */}
        <Typography variant="caption" color="text.secondary">
          Generation runs on the server. You can close this page — it will keep
          going.
        </Typography>
      </Stack>
    </Paper>
  );
}
